import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocomplete input backed by /api/public/geocode/suggest (Nominatim, ES-biased).
 * Controlled: parent owns `value` and receives every keystroke via `onChange(newValue)`.
 * When a suggestion is picked, `onChange(displayName)` fires with the exact address.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  className = "",
  testId,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const lastPickedRef = useRef("");

  // Debounced fetch
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3 || q === lastPickedRef.current) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/public/geocode/suggest`, {
          params: { q, limit: 6 },
        });
        setSuggestions(data?.results || []);
        setOpen(true);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (s) => {
    lastPickedRef.current = s.display_name;
    onChange(s.display_name);
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => value && value.length >= 3 && suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          data-testid={testId}
          className={`w-full h-11 px-3 pr-9 border border-black rounded-none bg-white text-sm focus:outline-none focus:ring-2 focus:ring-black/20 ${className}`}
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">
          {loading ? <Loader2 className="animate-spin" size={16} /> : <MapPin size={16} />}
        </div>
      </div>

      {open && !loading && suggestions.length === 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border-2 border-black p-3 text-xs text-zinc-600" data-testid={testId ? `${testId}-no-results` : undefined}>
          No encontramos esa dirección. Prueba con el nombre del hotel o lugar, o con calle + número + ciudad. También puedes dejarla escrita tal cual.
        </div>
      )}

      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 bg-white border-2 border-black shadow-lg max-h-72 overflow-y-auto"
          data-testid={testId ? `${testId}-suggestions` : undefined}
        >
          {suggestions.map((s, i) => (
            <li key={`${s.lat}-${s.lng}-${i}`}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(s);
                }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-zinc-100 last:border-b-0 flex items-start gap-2 transition-colors ${
                  activeIdx === i ? "bg-[#F5B301]/20" : "hover:bg-zinc-50"
                }`}
                data-testid={testId ? `${testId}-suggestion-${i}` : undefined}
              >
                <MapPin size={14} className="mt-0.5 text-zinc-500 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold truncate">{s.primary}</div>
                  {s.secondary && (
                    <div className="text-xs text-zinc-500 truncate">{s.secondary}</div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
